import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../../lib/supabase';
import SuperAdminLayout from '../components/SuperAdminLayout';
import CreatorNetworkIdsEditor from '../../../components/tcn-admin/CreatorNetworkIdsEditor';
import {
  FEATURE_GROUPS,
  FEATURES_BY_GROUP,
  GENERIC_DEFAULT_FEATURES,
  BLACKOS_PRESET_FEATURES,
  MINIMAL_PRESET_FEATURES,
} from '../../../lib/featureCatalog';

// Legacy feature slugs — kept here so existing tenants (TCN especially) don't
// lose their flags. These predate the 42-feature Generic catalog and are
// progressively being replaced by the new `pages.*` slugs.
// Reference : CLAUDE_GENERIC_MIGRATION.md §2
const LEGACY_FEATURES = [
  'morning_briefing',
  'tier_tasks',
  'payout',
  'creator_portal',
  'tiktok_oauth',
  'multi_server',
  'cashback',
  'tournament',
  'violations',
  'recruitment_links',
  'backstage_scraping',
  'whatsapp_notifications',
  'websocket_live',
  'lark_bot',
  'streamer_discovery',
];

const ROLES = ['super_admin', 'tenant_admin', 'agency_owner', 'agent', 'creator'];

// Languages supported by the platform — super_admin can enable/disable per tenant.
const SUPPORTED_LANGUAGES = ['fr', 'en', 'it', 'de', 'ar'];

// Full list of recruitment pipeline statuses (the union from CLAUDE_TCN.md §0).
// super_admin selects which subset is enabled per tenant.
const ALL_RECRUITMENT_STATUSES = [
  'prospect',
  'contacted',
  'replied',
  'interested',
  'negotiating',
  'onboarding',
  'signed',
  'onboarded',
  'declined',
  'rejected',
];

const TARGET_STRATEGIES = ['maintain_first', 'rank_up_only'];

// Known servers (matches `public.servers` seed). Hardcoded here for the multi-select
// to avoid an extra fetch — keep in sync with the servers seed migration.
const KNOWN_SERVERS = ['MENA', 'FR+', 'IT+', 'DE+', 'US+'];

const SERVER_SCOPES = ['global', 'per_agency'];

const TabButton = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
      active
        ? 'border-accent text-accent'
        : 'border-transparent text-muted-foreground hover:text-foreground'
    }`}
  >
    {children}
  </button>
);

const Toggle = ({ checked, onChange, disabled }) => (
  <button
    type="button"
    onClick={() => !disabled && onChange(!checked)}
    disabled={disabled}
    className={`relative w-10 h-6 rounded-full transition-colors ${
      checked ? 'bg-accent' : 'bg-muted'
    } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    aria-pressed={checked}
  >
    <span
      className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-card shadow-elevation-1 transition-transform ${
        checked ? 'translate-x-4' : ''
      }`}
    />
  </button>
);

const SuperAdminTenantDetail = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const [tenant, setTenant] = useState(null);
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(async () => {
    const [tenantRes, agenciesRes, managersRes, creatorsRes] = await Promise.all([
      supabase.from('tenants').select('*').eq('id', id).single(),
      supabase.from('agencies').select('id, name, slug, servers, active, creator_network_ids').eq('tenant_id', id),
      // Per-agency counts (managers/agents + creators)
      supabase.from('profiles').select('id, agency_id').in('role', ['manager', 'agent']).eq('tenant_id', id),
      supabase.from('creators').select('id, agency_id').eq('tenant_id', id).neq('status', 'departed'),
    ]);
    if (tenantRes.error || !tenantRes.data) {
      setNotFound(true);
    } else {
      setTenant(tenantRes.data);
      // Enrich agencies with counts
      const managers = managersRes.data || [];
      const creators = creatorsRes.data || [];
      const enriched = (agenciesRes.data || []).map((a) => ({
        ...a,
        managersCount: managers.filter((m) => m.agency_id === a.id).length,
        creatorsCount: creators.filter((c) => c.agency_id === a.id).length,
      }));
      setAgencies(enriched);
    }
    setLoading(false);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const updateFeature = async (featureName, value) => {
    setSaving(true);
    const updated = { ...(tenant.features || {}), [featureName]: value };
    const { error } = await supabase
      .from('tenants')
      .update({ features: updated })
      .eq('id', tenant.id);
    if (!error) {
      setTenant({ ...tenant, features: updated });
      setSavedAt(Date.now());
    }
    setSaving(false);
  };

  // Apply a preset (object slug→bool). Merges with existing features so legacy
  // slugs are not lost.
  const applyFeaturePreset = async (preset, presetName) => {
    if (!window.confirm(`Apply preset "${presetName}" to tenant "${tenant.slug}"? Existing legacy slugs are preserved.`)) {
      return;
    }
    setSaving(true);
    const updated = { ...(tenant.features || {}), ...preset };
    const { error } = await supabase
      .from('tenants')
      .update({ features: updated })
      .eq('id', tenant.id);
    if (!error) {
      setTenant({ ...tenant, features: updated });
      setSavedAt(Date.now());
    }
    setSaving(false);
  };

  // Bulk toggle for a section (set every feature of a group to value).
  const toggleSection = async (groupId, value) => {
    const slugs = (FEATURES_BY_GROUP[groupId] || []).map((f) => f.slug);
    if (slugs.length === 0) return;
    setSaving(true);
    const updated = { ...(tenant.features || {}) };
    for (const s of slugs) updated[s] = value;
    const { error } = await supabase
      .from('tenants')
      .update({ features: updated })
      .eq('id', tenant.id);
    if (!error) {
      setTenant({ ...tenant, features: updated });
      setSavedAt(Date.now());
    }
    setSaving(false);
  };

  // Update one or more top-level columns on the tenant row (used by the Flags tab).
  const updateTenantColumns = async (patch) => {
    setSaving(true);
    const { error } = await supabase
      .from('tenants')
      .update(patch)
      .eq('id', tenant.id);
    if (!error) {
      setTenant({ ...tenant, ...patch });
      setSavedAt(Date.now());
    }
    setSaving(false);
  };

  // Update a single key in tenant.permission_matrix without touching pages/actions.
  const updatePermissionMatrixFlag = async (key, value) => {
    setSaving(true);
    const matrix = JSON.parse(JSON.stringify(tenant.permission_matrix || {}));
    matrix[key] = value;
    const { error } = await supabase
      .from('tenants')
      .update({ permission_matrix: matrix })
      .eq('id', tenant.id);
    if (!error) {
      setTenant({ ...tenant, permission_matrix: matrix });
      setSavedAt(Date.now());
    }
    setSaving(false);
  };

  const updatePagePermission = async (page, role, allowed) => {
    setSaving(true);
    const matrix = JSON.parse(JSON.stringify(tenant.permission_matrix || { pages: {}, actions: {} }));
    if (!matrix.pages) matrix.pages = {};
    const list = new Set(matrix.pages[page] || []);
    if (allowed) list.add(role); else list.delete(role);
    matrix.pages[page] = Array.from(list);
    const { error } = await supabase
      .from('tenants')
      .update({ permission_matrix: matrix })
      .eq('id', tenant.id);
    if (!error) {
      setTenant({ ...tenant, permission_matrix: matrix });
      setSavedAt(Date.now());
    }
    setSaving(false);
  };

  if (notFound) return <Navigate to="/superadmin/tenants" replace />;

  if (loading || !tenant) {
    return (
      <SuperAdminLayout title={t('superadmin.loading', 'Loading...')}>
        <div className="space-y-3">
          <div className="h-12 rounded-xl skeleton" />
          <div className="h-48 rounded-xl skeleton" />
        </div>
      </SuperAdminLayout>
    );
  }

  return (
    <SuperAdminLayout
      title={tenant.name}
      subtitle={`slug: ${tenant.slug} · plan: ${tenant.plan}`}
      actions={
        <Link
          to="/superadmin/tenants"
          className="px-3 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          ← {t('superadmin.tenants.backToList', 'Back to tenants')}
        </Link>
      }
    >
      {/* Tabs */}
      <div className="border-b border-border mb-6">
        <div className="flex gap-1 overflow-x-auto">
          <TabButton active={tab === 'overview'} onClick={() => setTab('overview')}>
            {t('superadmin.tenant.tabOverview', 'Overview')}
          </TabButton>
          <TabButton active={tab === 'features'} onClick={() => setTab('features')}>
            {t('superadmin.tenant.tabFeatures', 'Features')}
          </TabButton>
          <TabButton active={tab === 'permissions'} onClick={() => setTab('permissions')}>
            {t('superadmin.tenant.tabPermissions', 'Page access')}
          </TabButton>
          <TabButton active={tab === 'flags'} onClick={() => setTab('flags')}>
            {t('superadmin.tenant.tabFlags', 'Flags')}
          </TabButton>
          <TabButton active={tab === 'agencies'} onClick={() => setTab('agencies')}>
            {t('superadmin.tenant.tabAgencies', 'Agencies')} ({agencies.length})
          </TabButton>
        </div>
      </div>

      {/* Save indicator */}
      {(saving || savedAt) && (
        <div className="mb-4 text-xs text-muted-foreground">
          {saving
            ? t('superadmin.saving', 'Saving...')
            : savedAt && t('superadmin.savedAt', 'Saved {{time}}', {
                time: new Date(savedAt).toLocaleTimeString(),
              })}
        </div>
      )}

      {/* Overview */}
      {tab === 'overview' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="rounded-2xl bg-card border border-border p-5">
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-3">
              {t('superadmin.tenant.identity', 'Identity')}
            </div>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between gap-4">
                <dt className="text-muted-foreground">{t('superadmin.tenant.name', 'Name')}</dt>
                <dd className="text-foreground font-medium">{tenant.name}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-muted-foreground">{t('superadmin.tenant.slug', 'Slug')}</dt>
                <dd className="text-foreground font-data">{tenant.slug}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-muted-foreground">{t('superadmin.tenant.plan', 'Plan')}</dt>
                <dd className="text-foreground">{tenant.plan}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-muted-foreground">{t('superadmin.tenant.status', 'Status')}</dt>
                <dd>
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${
                    tenant.active ? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground'
                  }`}>
                    {tenant.active ? t('superadmin.active', 'Active') : t('superadmin.inactive', 'Inactive')}
                  </span>
                </dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-muted-foreground">{t('superadmin.tenant.createdAt', 'Created')}</dt>
                <dd className="text-foreground text-xs">{new Date(tenant.created_at).toLocaleString()}</dd>
              </div>
            </dl>
          </div>

          <div className="rounded-2xl bg-card border border-border p-5">
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-3">
              {t('superadmin.tenant.scope', 'Scope (R2 reminder)')}
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {t(
                'superadmin.tenant.r2Note',
                'Super admin sees metadata only — never financial data (diamonds, revenue, payouts, commissions). Financial columns are AES-256 encrypted in DB and have no super_admin RLS policy.'
              )}
            </p>
          </div>
        </div>
      )}

      {/* Features matrix — 42-feature Generic catalog grouped by section */}
      {tab === 'features' && (
        <FeaturesMatrix
          tenant={tenant}
          saving={saving}
          onUpdateFeature={updateFeature}
          onApplyPreset={applyFeaturePreset}
          onToggleSection={toggleSection}
        />
      )}

      {/* Permissions matrix */}
      {tab === 'permissions' && (
        <PermissionsMatrix
          tenant={tenant}
          roles={ROLES}
          onToggle={updatePagePermission}
          saving={saving}
        />
      )}

      {/* Tenant flags */}
      {tab === 'flags' && (
        <TenantFlagsPanel
          tenant={tenant}
          saving={saving}
          onUpdateColumns={updateTenantColumns}
          onUpdateMatrixFlag={updatePermissionMatrixFlag}
        />
      )}

      {/* Agencies */}
      {tab === 'agencies' && (
        <div className="space-y-3">
          {/* Server scope notice */}
          {tenant.server_scope === 'global' && (
            <div className="rounded-xl bg-primary/5 border border-primary/20 px-4 py-3 text-sm text-foreground">
              <span className="font-medium">{t('superadmin.tenant.serverScopeGlobal', 'Server scope: global')}</span>
              {' — '}
              {t(
                'superadmin.tenant.serverScopeGlobalNote',
                'all agencies of this tenant inherit the global server list set in Flags.'
              )}
              {' '}
              <span className="text-muted-foreground">[{(tenant.global_servers || []).join(', ') || 'none'}]</span>
            </div>
          )}
          {tenant.server_scope === 'per_agency' && (
            <div className="rounded-xl bg-muted/40 border border-border px-4 py-3 text-sm text-muted-foreground">
              {t('superadmin.tenant.serverScopePerAgency', 'Server scope: per-agency — each agency picks its own servers below.')}
            </div>
          )}

          <AddAgencyForm tenantId={id} onCreated={load} />

          <div className="rounded-2xl bg-card border border-border overflow-hidden">
            {agencies.length === 0 ? (
              <div className="px-5 py-12 text-center text-muted-foreground text-sm">
                {t('superadmin.tenant.agenciesEmpty', 'This tenant has no agencies yet.')}
              </div>
            ) : (
              <div className="divide-y divide-border">
                {agencies.map((a) => (
                  <AgencyRow
                    key={a.id}
                    agency={a}
                    tenant={tenant}
                    onChange={load}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </SuperAdminLayout>
  );
};

/**
 * FeaturesMatrix — 42-feature Generic catalog, grouped by section.
 * See CLAUDE_GENERIC_MIGRATION.md §6 + src/lib/featureCatalog.js.
 *
 *  - Each section has its own card + bulk toggle (all on / all off).
 *  - Each feature row has badges (role, source) + toggle.
 *  - 3 presets accessible at top: Black OS / Generic Default / Minimal.
 *  - Legacy slugs (pre-catalog) live in a collapsed section at the bottom.
 */
const SOURCE_BADGE = {
  excel:    { label: 'Excel',    cls: 'bg-success/10 text-success' },
  manual:   { label: 'Manual',   cls: 'bg-muted text-muted-foreground' },
  mixed:    { label: 'Mixed',    cls: 'bg-primary/10 text-primary' },
  scraping: { label: 'Scraping', cls: 'bg-warning/10 text-warning' },
  api:      { label: 'API',      cls: 'bg-accent/10 text-accent' },
};

const ROLE_BADGE_CLS = 'bg-muted text-muted-foreground/80';

const FeaturesMatrix = ({ tenant, saving, onUpdateFeature, onApplyPreset, onToggleSection }) => {
  const { t } = useTranslation();
  const features = tenant.features || {};

  // Count how many of the 42 catalog slugs are ON
  const catalogSlugs = FEATURE_GROUPS.flatMap((g) => (FEATURES_BY_GROUP[g.id] || []).map((f) => f.slug));
  const onCount = catalogSlugs.filter((s) => Boolean(features[s])).length;

  // Detect legacy slugs that have a value but are not in the new catalog
  const legacySlugsWithValue = LEGACY_FEATURES.filter((s) => s in features);

  return (
    <div className="space-y-5">
      {/* Counter + Presets */}
      <div className="rounded-2xl bg-card border border-border p-5">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">
              {t('superadmin.tenant.features.activeCount', 'Active features')}
            </div>
            <div className="text-2xl font-heading font-bold text-foreground tabular-nums mt-1">
              {onCount} / {catalogSlugs.length}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => onApplyPreset(BLACKOS_PRESET_FEATURES, 'Black OS — all ON')}
              disabled={saving}
              className="px-3 py-1.5 text-xs font-medium rounded-lg border border-accent text-accent hover:bg-accent/10 transition-colors disabled:opacity-60"
            >
              {t('superadmin.tenant.features.presetBlackos', 'Apply Black OS (all 42 ON)')}
            </button>
            <button
              type="button"
              onClick={() => onApplyPreset(GENERIC_DEFAULT_FEATURES, 'Generic Default')}
              disabled={saving}
              className="px-3 py-1.5 text-xs font-medium rounded-lg border border-border text-foreground hover:border-accent/50 transition-colors disabled:opacity-60"
            >
              {t('superadmin.tenant.features.presetDefault', 'Apply Generic Default (28 ON / 14 OFF)')}
            </button>
            <button
              type="button"
              onClick={() => onApplyPreset(MINIMAL_PRESET_FEATURES, 'Minimal (Phase 1 strict)')}
              disabled={saving}
              className="px-3 py-1.5 text-xs font-medium rounded-lg border border-border text-foreground hover:border-accent/50 transition-colors disabled:opacity-60"
            >
              {t('superadmin.tenant.features.presetMinimal', 'Apply Minimal (no scraping/api)')}
            </button>
          </div>
        </div>
      </div>

      {/* Groups */}
      {FEATURE_GROUPS.map((group) => {
        const items = FEATURES_BY_GROUP[group.id] || [];
        if (items.length === 0) return null;
        const allOn = items.every((f) => Boolean(features[f.slug]));
        const allOff = items.every((f) => !features[f.slug]);
        return (
          <div key={group.id} className="rounded-2xl bg-card border border-border overflow-hidden">
            <div className="flex items-center justify-between gap-4 px-5 py-3 bg-surface-2 border-b border-border">
              <div className="font-heading font-semibold text-foreground text-sm">
                {t(`superadmin.tenant.features.group.${group.id}`, group.label)}
                <span className="ml-2 text-xs text-muted-foreground font-normal">({items.length})</span>
              </div>
              <div className="flex gap-1">
                <button
                  type="button"
                  onClick={() => onToggleSection(group.id, true)}
                  disabled={saving || allOn}
                  className="px-2 py-1 text-[10px] uppercase tracking-widest font-semibold rounded text-success border border-success/30 hover:bg-success/10 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {t('superadmin.tenant.features.allOn', 'All on')}
                </button>
                <button
                  type="button"
                  onClick={() => onToggleSection(group.id, false)}
                  disabled={saving || allOff}
                  className="px-2 py-1 text-[10px] uppercase tracking-widest font-semibold rounded text-muted-foreground border border-border hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {t('superadmin.tenant.features.allOff', 'All off')}
                </button>
              </div>
            </div>
            <div className="divide-y divide-border">
              {items.map((feat) => {
                const enabled = Boolean(features[feat.slug]);
                const source = SOURCE_BADGE[feat.source] || SOURCE_BADGE.manual;
                return (
                  <div key={feat.slug} className="flex items-center justify-between gap-4 px-5 py-3.5">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-foreground text-sm">{feat.label}</span>
                        <span className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded ${source.cls}`}>
                          {source.label}
                        </span>
                        {feat.roles.map((r) => (
                          <span
                            key={r}
                            className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded ${ROLE_BADGE_CLS}`}
                          >
                            {r}
                          </span>
                        ))}
                      </div>
                      <div className="text-[11px] text-muted-foreground font-data mt-0.5">{feat.slug}</div>
                      {feat.pageBlackOS && (
                        <div className="text-[10px] text-muted-foreground/60 font-data mt-0.5">
                          ← blackos/{feat.pageBlackOS}
                        </div>
                      )}
                    </div>
                    <Toggle
                      checked={enabled}
                      onChange={(v) => onUpdateFeature(feat.slug, v)}
                      disabled={saving}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Legacy slugs — collapsed, only shown if any present on tenant */}
      {legacySlugsWithValue.length > 0 && (
        <details className="rounded-2xl bg-card border border-border">
          <summary className="cursor-pointer select-none px-5 py-3 flex items-center justify-between gap-3">
            <div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">
                {t('superadmin.tenant.features.legacy', 'Legacy slugs')}
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {t('superadmin.tenant.features.legacyNote', 'Pre-catalog slugs. Kept for backward-compat (3 JSX files still use them). Will be migrated sprint by sprint.')}
              </div>
            </div>
            <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-muted text-muted-foreground">
              {legacySlugsWithValue.length} slugs
            </span>
          </summary>
          <div className="border-t border-border divide-y divide-border">
            {LEGACY_FEATURES.filter((s) => s in features).map((feat) => {
              const enabled = Boolean(features[feat]);
              return (
                <div key={feat} className="flex items-center justify-between gap-4 px-5 py-3 opacity-80">
                  <div>
                    <div className="font-medium text-foreground font-data text-sm">{feat}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">legacy</div>
                  </div>
                  <Toggle
                    checked={enabled}
                    onChange={(v) => onUpdateFeature(feat, v)}
                    disabled={saving}
                  />
                </div>
              );
            })}
          </div>
        </details>
      )}
    </div>
  );
};

const PermissionsMatrix = ({ tenant, roles, onToggle, saving }) => {
  const { t } = useTranslation();
  const pages = Object.keys(tenant.permission_matrix?.pages || {}).sort();

  if (pages.length === 0) {
    return (
      <div className="rounded-2xl bg-card border border-dashed border-border p-8 text-center text-muted-foreground text-sm">
        {t(
          'superadmin.tenant.permissionsEmpty',
          'No pages configured in the permission matrix yet. Pages must first be defined in tenant.permission_matrix.pages (seeded or via SQL).'
        )}
      </div>
    );
  }

  return (
    <div className="rounded-2xl bg-card border border-border overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-surface-2">
            <th className="text-left px-4 py-3 text-xs uppercase tracking-widest text-muted-foreground font-semibold">
              {t('superadmin.tenant.page', 'Page')}
            </th>
            {roles.map((role) => (
              <th
                key={role}
                className="text-center px-3 py-3 text-xs uppercase tracking-widest text-muted-foreground font-semibold"
              >
                {role}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {pages.map((page) => {
            const list = tenant.permission_matrix?.pages?.[page] || [];
            return (
              <tr key={page} className="border-b border-border last:border-b-0">
                <td className="px-4 py-3 font-data text-foreground">{page}</td>
                {roles.map((role) => {
                  const allowed = list.includes(role);
                  return (
                    <td key={role} className="px-3 py-3 text-center">
                      <button
                        type="button"
                        onClick={() => onToggle(page, role, !allowed)}
                        disabled={saving}
                        className={`w-6 h-6 rounded-md border transition-colors ${
                          allowed
                            ? 'bg-accent border-accent'
                            : 'bg-card border-border hover:border-accent'
                        }`}
                        aria-pressed={allowed}
                        aria-label={`${role} → ${page}: ${allowed ? 'allowed' : 'denied'}`}
                      >
                        {allowed && (
                          <svg width="14" height="14" viewBox="0 0 14 14" className="mx-auto" fill="none">
                            <path
                              d="M3 7l3 3 5-6"
                              stroke="white"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                      </button>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

/**
 * Tenant Flags panel — system-level toggles for the tenant.
 *
 *  - permission_matrix.allow_agency_target_override (bool)
 *  - enabled_languages (text[])
 *  - recruitment_pipeline_statuses (text[])
 *  - target_strategy (enum)
 *
 * Each setting commits to DB independently on change (auto-save).
 */
// One agency row in the Agencies tab. Shows counts + server chips.
// Servers are read-only when tenant.server_scope='global' (inherits from tenant).
// Servers are editable when tenant.server_scope='per_agency'.
// Création d'agence + owner (via Edge Function admin-create-agency).
// Accessible super_admin (et tenant_admin scopé, côté fonction).
function AddAgencyForm({ tenantId, onCreated }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [servers, setServers] = useState(['MENA']);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const toggleServer = (s) => setServers((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));

  const submit = async () => {
    setBusy(true); setMsg(null);
    try {
      const { data, error } = await supabase.functions.invoke('admin-create-agency', {
        body: { tenantId, name, servers, ownerEmail: email, ownerPassword: password, ownerName: `${name} owner` },
      });
      if (error || data?.error) throw new Error(data?.error || error.message);
      setMsg({ ok: true, text: `Agency "${data.agency.name}" created · owner ${data.owner.email}` });
      setName(''); setEmail(''); setPassword(''); setServers(['MENA']);
      onCreated?.();
    } catch (e) {
      setMsg({ ok: false, text: e.message || 'Creation failed' });
    } finally { setBusy(false); }
  };

  const inputCls = 'w-full px-3 py-2 rounded-lg bg-background border border-border text-base focus:outline-none focus:ring-1 focus:ring-primary';

  return (
    <div className="rounded-2xl bg-card border border-border p-4">
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between text-sm font-semibold text-foreground">
        <span>+ Add agency</span>
        <span className="text-muted-foreground text-xs">{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div className="mt-4 space-y-3">
          <input className={inputCls} placeholder="Agency name" value={name} onChange={(e) => setName(e.target.value)} />
          <input className={inputCls} type="email" placeholder="Owner email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <input className={inputCls} type="text" placeholder="Owner password (min 8 chars)" value={password} onChange={(e) => setPassword(e.target.value)} />
          <div>
            <div className="text-xs text-muted-foreground mb-1.5">Servers</div>
            <div className="flex flex-wrap gap-2">
              {KNOWN_SERVERS.map((s) => (
                <button key={s} type="button" onClick={() => toggleServer(s)}
                  className={`px-3 py-1.5 rounded-full text-xs border ${servers.includes(s) ? 'bg-primary text-primary-foreground border-primary' : 'bg-background text-muted-foreground border-border'}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>
          {msg && <div className={`text-xs rounded-lg px-3 py-2 ${msg.ok ? 'bg-success/10 text-success' : 'bg-error/10 text-error'}`}>{msg.text}</div>}
          <button type="button" onClick={submit} disabled={busy}
            className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium disabled:opacity-60">
            {busy ? 'Creating…' : 'Create agency + owner'}
          </button>
        </div>
      )}
    </div>
  );
}

const AgencyRow = ({ agency, tenant, onChange }) => {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const isGlobal = tenant.server_scope === 'global';
  // Effective servers shown — global list when scope=global, otherwise the agency's own.
  const effectiveServers = isGlobal ? (tenant.global_servers || []) : (agency.servers || []);

  const toggleServer = async (server) => {
    if (isGlobal || saving) return;
    const current = new Set(agency.servers || []);
    if (current.has(server)) current.delete(server);
    else current.add(server);
    setSaving(true);
    setError(null);
    try {
      const { error: dbError } = await supabase
        .from('agencies')
        .update({ servers: Array.from(current) })
        .eq('id', agency.id);
      if (dbError) throw dbError;
      await onChange();
    } catch (e) {
      setError(e.message || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="px-5 py-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="font-medium text-foreground">{agency.name}</div>
          <div className="text-xs text-muted-foreground font-data">{agency.slug}</div>
        </div>
        <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${
          agency.active ? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground'
        }`}>
          {agency.active ? t('superadmin.active', 'Active') : t('superadmin.inactive', 'Inactive')}
        </span>
      </div>

      <div className="mt-3 flex items-center gap-4 flex-wrap text-xs">
        <span className="text-muted-foreground">
          <span className="font-semibold text-foreground tabular-nums">{agency.managersCount}</span>{' '}
          {t('superadmin.tenant.managers', 'managers')}
        </span>
        <span className="text-muted-foreground">
          <span className="font-semibold text-foreground tabular-nums">{agency.creatorsCount}</span>{' '}
          {t('superadmin.tenant.creators', 'creators')}
        </span>
      </div>

      <div className="mt-3">
        <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2">
          {t('superadmin.tenant.servers', 'Servers')}
          {isGlobal && (
            <span className="ml-2 normal-case font-normal text-muted-foreground/80">
              ({t('superadmin.tenant.fromTenantGlobal', 'from tenant globals')})
            </span>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {KNOWN_SERVERS.map((server) => {
            const on = effectiveServers.includes(server);
            const baseClasses = 'px-3 py-1 rounded-full text-xs font-medium transition-colors';
            if (isGlobal) {
              // Read-only chips
              return (
                <span
                  key={server}
                  className={`${baseClasses} ${
                    on
                      ? 'bg-primary/10 text-primary'
                      : 'bg-muted text-muted-foreground/60'
                  } ${on ? '' : 'opacity-50'}`}
                >
                  {server}
                </span>
              );
            }
            return (
              <button
                key={server}
                type="button"
                onClick={() => toggleServer(server)}
                disabled={saving}
                className={`${baseClasses} border ${
                  on
                    ? 'bg-accent text-accent-foreground border-accent'
                    : 'bg-card border-border text-muted-foreground hover:border-accent/50'
                } ${saving ? 'opacity-60 cursor-not-allowed' : ''}`}
              >
                {server}
              </button>
            );
          })}
        </div>
        {error && <p className="text-xs text-error mt-1">{error}</p>}
      </div>

      {/* Creator Network IDs (TikTok Backstage faction_id) */}
      <div className="mt-4">
        <CreatorNetworkIdsEditor
          agency={{ ...agency, servers: effectiveServers }}
          onSaved={() => onChange()}
        />
      </div>
    </div>
  );
};

const TenantFlagsPanel = ({ tenant, saving, onUpdateColumns, onUpdateMatrixFlag }) => {
  const { t } = useTranslation();

  const allowOverride = Boolean(
    tenant.permission_matrix?.allow_agency_target_override
  );
  const enabledLanguages = tenant.enabled_languages || [];
  const recruitmentStatuses = tenant.recruitment_pipeline_statuses || [];
  const targetStrategy = tenant.target_strategy || 'rank_up_only';
  const serverScope = tenant.server_scope || 'per_agency';
  const globalServers = tenant.global_servers || [];

  const toggleLanguage = (lang) => {
    const next = new Set(enabledLanguages);
    if (next.has(lang)) next.delete(lang);
    else next.add(lang);
    onUpdateColumns({ enabled_languages: Array.from(next) });
  };

  const toggleStatus = (status) => {
    const next = new Set(recruitmentStatuses);
    if (next.has(status)) next.delete(status);
    else next.add(status);
    onUpdateColumns({ recruitment_pipeline_statuses: Array.from(next) });
  };

  const toggleGlobalServer = (server) => {
    const next = new Set(globalServers);
    if (next.has(server)) next.delete(server);
    else next.add(server);
    onUpdateColumns({ global_servers: Array.from(next) });
  };

  return (
    <div className="space-y-5">
      {/* allow_agency_target_override */}
      <div className="rounded-2xl bg-card border border-border p-5">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="font-medium text-foreground">
              {t(
                'superadmin.tenant.flags.allowOverride',
                'Allow agency target override'
              )}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5 font-data">
              permission_matrix.allow_agency_target_override
            </div>
            <p className="text-xs text-muted-foreground mt-2 max-w-xl">
              {t(
                'superadmin.tenant.flags.allowOverrideHelp',
                'When ON, agency_admin / agency_owner can override tenant-level objective rules at the agency or agent level. When OFF, only the tenant_admin rules apply.'
              )}
            </p>
          </div>
          <Toggle
            checked={allowOverride}
            onChange={(v) =>
              onUpdateMatrixFlag('allow_agency_target_override', v)
            }
            disabled={saving}
          />
        </div>
      </div>

      {/* enabled_languages */}
      <div className="rounded-2xl bg-card border border-border p-5">
        <div className="font-medium text-foreground mb-1">
          {t('superadmin.tenant.flags.enabledLanguages', 'Enabled languages')}
        </div>
        <div className="text-xs text-muted-foreground mb-3 font-data">
          enabled_languages
        </div>
        <p className="text-xs text-muted-foreground mb-4 max-w-xl">
          {t(
            'superadmin.tenant.flags.enabledLanguagesHelp',
            'Subset of NoiRock-supported languages exposed to this tenant. The frontend i18n picker only shows languages selected here.'
          )}
        </p>
        <div className="flex flex-wrap gap-2">
          {SUPPORTED_LANGUAGES.map((lang) => {
            const on = enabledLanguages.includes(lang);
            return (
              <button
                key={lang}
                type="button"
                onClick={() => toggleLanguage(lang)}
                disabled={saving}
                className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors uppercase tracking-widest ${
                  on
                    ? 'bg-accent/10 border-accent text-accent'
                    : 'bg-card border-border text-muted-foreground hover:border-accent'
                } disabled:opacity-50`}
                aria-pressed={on}
              >
                {lang}
              </button>
            );
          })}
        </div>
      </div>

      {/* recruitment_pipeline_statuses */}
      <div className="rounded-2xl bg-card border border-border p-5">
        <div className="font-medium text-foreground mb-1">
          {t(
            'superadmin.tenant.flags.recruitmentStatuses',
            'Recruitment pipeline statuses'
          )}
        </div>
        <div className="text-xs text-muted-foreground mb-3 font-data">
          recruitment_pipeline_statuses
        </div>
        <p className="text-xs text-muted-foreground mb-4 max-w-xl">
          {t(
            'superadmin.tenant.flags.recruitmentStatusesHelp',
            'Subset of recruitment statuses available to this tenant in the pipeline UI.'
          )}
        </p>
        <div className="flex flex-wrap gap-2">
          {ALL_RECRUITMENT_STATUSES.map((status) => {
            const on = recruitmentStatuses.includes(status);
            return (
              <button
                key={status}
                type="button"
                onClick={() => toggleStatus(status)}
                disabled={saving}
                className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors font-data ${
                  on
                    ? 'bg-accent/10 border-accent text-accent'
                    : 'bg-card border-border text-muted-foreground hover:border-accent'
                } disabled:opacity-50`}
                aria-pressed={on}
              >
                {status}
              </button>
            );
          })}
        </div>
      </div>

      {/* target_strategy */}
      <div className="rounded-2xl bg-card border border-border p-5">
        <div className="font-medium text-foreground mb-1">
          {t('superadmin.tenant.flags.targetStrategy', 'Target strategy')}
        </div>
        <div className="text-xs text-muted-foreground mb-3 font-data">
          target_strategy
        </div>
        <p className="text-xs text-muted-foreground mb-4 max-w-xl">
          {t(
            'superadmin.tenant.flags.targetStrategyHelp',
            "'maintain_first' shows the maintain threshold first then switches to rank-up once met. 'rank_up_only' always shows the next-tier threshold."
          )}
        </p>
        <div className="flex flex-col gap-2">
          {TARGET_STRATEGIES.map((strat) => (
            <label
              key={strat}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl border cursor-pointer transition-colors ${
                targetStrategy === strat
                  ? 'border-accent bg-accent/5'
                  : 'border-border hover:border-accent/50'
              } ${saving ? 'opacity-60 pointer-events-none' : ''}`}
            >
              <input
                type="radio"
                name="target_strategy"
                value={strat}
                checked={targetStrategy === strat}
                onChange={() => onUpdateColumns({ target_strategy: strat })}
                className="text-accent focus:ring-accent"
              />
              <div>
                <div className="font-data text-sm text-foreground">{strat}</div>
                <div className="text-xs text-muted-foreground">
                  {strat === 'maintain_first'
                    ? t(
                        'superadmin.tenant.flags.maintainFirstDesc',
                        'Show maintain threshold first, then switch to rank-up'
                      )
                    : t(
                        'superadmin.tenant.flags.rankUpOnlyDesc',
                        'Always show the next-tier rank-up threshold'
                      )}
                </div>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* server_scope + global_servers — Migration 8 */}
      <div className="rounded-2xl bg-card border border-border p-5">
        <div className="font-medium text-foreground mb-1">
          {t('superadmin.tenant.flags.serverScope', 'Server scope')}
        </div>
        <div className="text-xs text-muted-foreground mb-1 font-data">
          tenants.server_scope
        </div>
        <p className="text-xs text-muted-foreground mb-4 max-w-xl">
          {t(
            'superadmin.tenant.flags.serverScopeHelp',
            "'global' = same server list applied to ALL agencies of this tenant. 'per_agency' = each agency picks its own servers (multi-server typical for Generic)."
          )}
        </p>
        <div className="flex flex-col gap-2 mb-5">
          {SERVER_SCOPES.map((scope) => (
            <label
              key={scope}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl border cursor-pointer transition-colors ${
                serverScope === scope
                  ? 'border-accent bg-accent/5'
                  : 'border-border hover:border-accent/50'
              } ${saving ? 'opacity-60 pointer-events-none' : ''}`}
            >
              <input
                type="radio"
                name="server_scope"
                value={scope}
                checked={serverScope === scope}
                onChange={() => onUpdateColumns({ server_scope: scope })}
                className="text-accent focus:ring-accent"
              />
              <div>
                <div className="font-data text-sm text-foreground">{scope}</div>
                <div className="text-xs text-muted-foreground">
                  {scope === 'global'
                    ? t('superadmin.tenant.flags.serverScopeGlobalDesc', 'Same servers for all agencies (e.g. TCN locked to MENA)')
                    : t('superadmin.tenant.flags.serverScopePerAgencyDesc', 'Each agency picks its own servers')}
                </div>
              </div>
            </label>
          ))}
        </div>

        {/* Global servers multi-select — visible only when scope=global */}
        {serverScope === 'global' && (
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2">
              {t('superadmin.tenant.flags.globalServers', 'Servers (applied to all agencies)')}
            </div>
            <div className="flex flex-wrap gap-2">
              {KNOWN_SERVERS.map((server) => {
                const on = globalServers.includes(server);
                return (
                  <button
                    key={server}
                    type="button"
                    onClick={() => toggleGlobalServer(server)}
                    disabled={saving}
                    className={`px-3 py-1.5 rounded-full border text-sm font-medium transition-colors ${
                      on
                        ? 'bg-accent text-accent-foreground border-accent'
                        : 'bg-card border-border text-muted-foreground hover:border-accent/50'
                    } ${saving ? 'opacity-60 cursor-not-allowed' : ''}`}
                  >
                    {server}
                  </button>
                );
              })}
            </div>
            {globalServers.length === 0 && (
              <p className="text-xs text-warning mt-2">
                {t('superadmin.tenant.flags.globalServersEmpty', '⚠️ No servers selected — agencies will have an empty roster.')}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SuperAdminTenantDetail;
